"use client"
import { Button } from "@cloudflare/kumo/components/button"
import { SensitiveInput } from "@cloudflare/kumo/components/sensitive-input"
import { Input } from "@cloudflare/kumo/components/input"
import { ArrowRightIcon } from "@phosphor-icons/react"
import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from "react"
import { useAuth, useClerk, useOrganizationList, useSignIn, useUser } from "@clerk/nextjs"
import { useRouter } from "next/navigation"
import {
  UNSUPPORTED_SESSION_TASK_MESSAGE,
  activateChosenOrganization,
  buildChooseWorkspaceView,
  nextExistingSessionHandoffPhase,
  resolveLoginFinalizeNavigation,
  resolveLoginSessionSurface,
  shouldRequestNextMembershipPage,
  type ChooseWorkspaceMembership,
  type ExistingSessionHandoffPhase,
} from "@/modules/authorization/login-choose-organization"
import {
  loginPathReturningTo,
  readLoginRedirectUrlParam,
  resolveLoginRedirectUrl,
} from "@/modules/authorization/login-redirect"
import { continueWithSingleSignOn, emailUsesSingleSignOn } from "@/modules/authorization/enterprise-sign-in"
import {
  StandaloneButton,
  StandaloneCard,
  StandaloneNote,
} from "@/components/kumo/standalone-card"
import { ChooseWorkspaceView } from "./choose-workspace-view"
type LoginStep = "password" | "verification" | "choose-organization"
type SignInStatus =
  | "needs_identifier"
  | "needs_first_factor"
  | "needs_second_factor"
  | "needs_client_trust"
  | "needs_protect_check"
  | "needs_new_password"
  | "complete"
type SignInFactor = {
  strategy?: string
}
export function LoginClient({ hasClerkAuthConfig }: { hasClerkAuthConfig: boolean }) {
  if (!hasClerkAuthConfig) {
    return <LoginUnavailable />
  }
  return <LoginController />
}
function LoginController() {
  const router = useRouter()
  const clerk = useClerk()
  const { signIn, fetchStatus } = useSignIn()
  const { isLoaded: isAuthLoaded, isSignedIn, sessionId } = useAuth()
  const [step, setStep] = useState<LoginStep>("password")
  const [errorMessage, setErrorMessage] = useState("")
  const [statusMessage, setStatusMessage] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [clerkInitTimedOut, setClerkInitTimedOut] = useState(false)
  const existingSessionHandoffPhase = useRef<ExistingSessionHandoffPhase>("pending")
  const isLoading = isSubmitting || fetchStatus === "fetching" || !isAuthLoaded
  const sessionSurface = resolveLoginSessionSurface({
    isAuthLoaded,
    isSignedIn: Boolean(isSignedIn),
    session: clerk.session,
    localStep: step,
  })
  useEffect(() => {
    if (!isSignedIn) {
      setStatusMessage("")
    }
  }, [isSignedIn])
  useEffect(() => {
    if (isAuthLoaded) {
      setClerkInitTimedOut(false)
      return
    }
    const timer = window.setTimeout(() => {
      setClerkInitTimedOut(true)
    }, 10000)
    return () => window.clearTimeout(timer)
  }, [isAuthLoaded])
  // Server cannot see hash fragments. Auto-handoff only for a session already
  // active when this page loaded; local password/MFA stays on finalizeSignIn.
  useEffect(() => {
    const nextPhase = nextExistingSessionHandoffPhase({
      phase: existingSessionHandoffPhase.current,
      isAuthLoaded,
      sessionSurfaceKind: sessionSurface.kind,
    })
    if (nextPhase === existingSessionHandoffPhase.current) return
    existingSessionHandoffPhase.current = nextPhase
    if (nextPhase === "started") {
      void activateExistingSession()
    }
  }, [isAuthLoaded, sessionSurface.kind])
  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (isLoading) return
    if (isSignedIn) {
      await resumeExistingClerkSession()
      return
    }
    const formData = new FormData(event.currentTarget)
    const emailAddress = String(formData.get("email") ?? "").trim()
    const password = String(formData.get("password") ?? "")
    setErrorMessage("")
    setStatusMessage("")
    setIsSubmitting(true)
    try {
      if (await startEnterpriseSignIn(emailAddress)) return
      if (!password) {
        setErrorMessage("Enter your password.")
        return
      }
      const result = await signIn.password({
        emailAddress,
        password,
      })
      if (result.error) {
        const message = readAuthError(result.error)
        if (isAlreadySignedInMessage(message)) {
          await resumeExistingClerkSession()
          return
        }
        setErrorMessage(message)
        return
      }
      const status = readSignInStatus(result, signIn.status)
      if (status === "complete") {
        await finalizeSignIn()
        return
      }
      if (status === "needs_client_trust" || status === "needs_second_factor") {
        await sendEmailVerificationCode(result)
        return
      }
      setErrorMessage("This account needs a sign-in step this page does not support yet.")
    } catch (error) {
      const message = readAuthError(error)
      if (isAlreadySignedInMessage(message)) {
        await resumeExistingClerkSession()
        return
      }
      setErrorMessage(message)
    } finally {
      setIsSubmitting(false)
    }
  }
  async function startEnterpriseSignIn(emailAddress: string) {
    if (!(await emailUsesSingleSignOn(signIn, emailAddress))) return false
    setStatusMessage("Opening your company sign-in.")
    const redirected = await continueWithSingleSignOn(signIn, `/login${window.location.search}`)
    if (redirected.error) {
      setStatusMessage("")
      setErrorMessage(readAuthError(redirected.error))
    }
    return true
  }
  async function handleVerificationSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (isLoading) return
    const formData = new FormData(event.currentTarget)
    const code = String(formData.get("code") ?? "").trim()
    setErrorMessage("")
    setStatusMessage("")
    setIsSubmitting(true)
    try {
      const result = await signIn.mfa.verifyEmailCode({ code })
      if (result.error) {
        setErrorMessage(readAuthError(result.error))
        return
      }
      const status = readSignInStatus(result, signIn.status)
      if (status === "complete") {
        await finalizeSignIn()
        return
      }
      setErrorMessage("The verification code was accepted, but sign-in still needs another step.")
    } catch (error) {
      setErrorMessage(readAuthError(error))
    } finally {
      setIsSubmitting(false)
    }
  }
  async function sendEmailVerificationCode(signInState?: unknown) {
    const factors = readSupportedSecondFactors(signInState, signIn.supportedSecondFactors)
    const supportsEmailCode = factors.some((factor) => factor.strategy === "email_code")
    if (!supportsEmailCode) {
      setErrorMessage(
        "This account needs an extra verification method this page does not support yet.",
      )
      return
    }
    const result = await signIn.mfa.sendEmailCode()
    if (result.error) {
      setErrorMessage(readAuthError(result.error))
      return
    }
    setStep("verification")
    setStatusMessage("We sent a verification code to your email. Enter it to finish signing in.")
  }
  async function resendVerificationCode(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    if (isLoading) return
    setErrorMessage("")
    setStatusMessage("")
    setIsSubmitting(true)
    try {
      await sendEmailVerificationCode()
    } catch (error) {
      setErrorMessage(readAuthError(error))
    } finally {
      setIsSubmitting(false)
    }
  }
  async function resetSignIn(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    setErrorMessage("")
    setStatusMessage("")
    setIsSubmitting(true)
    try {
      await signIn.reset()
      setStep("password")
    } catch (error) {
      setErrorMessage(readAuthError(error))
    } finally {
      setIsSubmitting(false)
    }
  }
  async function finalizeSignIn() {
    const result = await signIn.finalize({
      navigate: navigateToWorkspace,
    })
    if (result.error) {
      setErrorMessage(readAuthError(result.error))
    }
  }
  async function resumeExistingClerkSession() {
    const action = resolveLoginFinalizeNavigation(clerk.session)
    if (action.kind === "choose-organization") {
      setStatusMessage("")
      setErrorMessage("")
      setStep("choose-organization")
      return
    }
    if (action.kind === "unsupported-task") {
      setStatusMessage("")
      setErrorMessage(UNSUPPORTED_SESSION_TASK_MESSAGE)
      return
    }
    await activateExistingSession()
  }
  async function activateExistingSession() {
    const redirectUrl = readLoginRedirectTarget()
    setErrorMessage("")
    setStatusMessage("Opening the Ortelian Workspace.")
    setIsSubmitting(true)
    const fallbackNavigation = window.setTimeout(() => {
      window.location.assign(redirectUrl)
    }, 2000)
    try {
      const activeSessionId = sessionId ?? clerk.session?.id
      if (activeSessionId) {
        await clerk.setActive({
          session: activeSessionId,
          redirectUrl,
        })
      }
      window.clearTimeout(fallbackNavigation)
      window.location.assign(redirectUrl)
    } catch (error) {
      window.clearTimeout(fallbackNavigation)
      setStatusMessage("")
      setErrorMessage(readAuthError(error))
      setIsSubmitting(false)
    } finally {
      window.clearTimeout(fallbackNavigation)
    }
  }
  async function handleSignOut(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    if (isSubmitting) return
    setErrorMessage("")
    setStatusMessage("")
    setIsSubmitting(true)
    const message = await signOutKeepingReturn(clerk)
    if (message) {
      setErrorMessage(message)
      setIsSubmitting(false)
    }
  }
  function navigateToWorkspace({
    session: activeSession,
    decorateUrl,
  }: {
    session?: {
      currentTask?: unknown
    } | null
    decorateUrl: (url: string) => string
  }) {
    const navigation = resolveLoginFinalizeNavigation(activeSession)
    if (navigation.kind === "choose-organization") {
      setStatusMessage("")
      setErrorMessage("")
      setStep("choose-organization")
      return
    }
    if (navigation.kind === "unsupported-task") {
      setStatusMessage("")
      setErrorMessage(UNSUPPORTED_SESSION_TASK_MESSAGE)
      return
    }
    const url = decorateUrl(readLoginRedirectTarget())
    if (url.startsWith("http")) {
      window.location.href = url
      return
    }
    router.replace(url)
  }
  return (
    <StandaloneCard
      label="Sign in"
      title={
        sessionSurface.kind !== "choose-organization"
          ? step === "verification"
            ? "Check your email"
            : "Sign in to Ortelian"
          : undefined
      }
    >
                {sessionSurface.kind === "active-session" ? (
                  <div className="grid gap-6">
                    <StandaloneNote tone="info">You are already signed in.</StandaloneNote>

                    <LoginMessages errorMessage={errorMessage} statusMessage={statusMessage} />

                    <StandaloneButton
                      type="button"
                      icon={ArrowRightIcon}
                      disabled={isLoading}
                      onClick={activateExistingSession}
                    >
                      {isLoading ? "Opening workspace" : "Open workspace"}
                    </StandaloneButton>

                    <div className="flex flex-col items-start gap-4 min-[421px]:flex-row min-[421px]:items-center min-[421px]:justify-between">
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={handleSignOut}
                        disabled={isSubmitting}
                      >
                        Sign out
                      </Button>
                    </div>
                  </div>
                ) : sessionSurface.kind === "choose-organization" ? (
                  <ChooseOrganizationStep />
                ) : sessionSurface.kind === "unsupported-task" ? (
                  <div className="grid gap-6">
                    <LoginMessages
                      errorMessage={UNSUPPORTED_SESSION_TASK_MESSAGE}
                      statusMessage=""
                    />
                  </div>
                ) : sessionSurface.kind === "password" ? (
                  <form onSubmit={handleSubmit}>
                    <div className="grid gap-6">
                      <Input
                        id="login-email"
                        name="email"
                        type="email"
                        label="Email"
                        placeholder="name@company.com"
                        autoComplete="email"
                        size="lg"
                        autoFocus
                        required
                      />
                      <SensitiveInput
                        id="login-password"
                        name="password"
                        label="Password"
                        placeholder="Enter password"
                        autoComplete="current-password"
                        size="lg"
                      />

                      <LoginMessages
                        errorMessage={
                          clerkInitTimedOut && !isAuthLoaded
                            ? "Authentication is taking longer than expected. Refresh the page or try again in a private window."
                            : errorMessage
                        }
                        statusMessage={statusMessage}
                      />

                      <StandaloneButton
                        type="submit"
                        icon={ArrowRightIcon}
                        disabled={isLoading}
                      >
                        {!isAuthLoaded ? "Loading" : isLoading ? "Signing in" : "Sign in"}
                      </StandaloneButton>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleVerificationSubmit}>
                    <div className="grid gap-6">
                      <p className="text-kumo-subtle">
                        We need one more check before opening the workspace.
                      </p>
                      <Input
                        id="login-code"
                        name="code"
                        type="text"
                        label="Verification code"
                        placeholder="Enter code"
                        autoComplete="one-time-code"
                        size="lg"
                        autoFocus
                        required
                      />

                      <LoginMessages errorMessage={errorMessage} statusMessage={statusMessage} />

                      <StandaloneButton
                        type="submit"
                        icon={ArrowRightIcon}
                        disabled={isLoading}
                      >
                        {isLoading ? "Verifying" : "Verify and sign in"}
                      </StandaloneButton>

                      <div className="flex flex-col items-start gap-4 min-[421px]:flex-row min-[421px]:items-center min-[421px]:justify-between">
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          onClick={resendVerificationCode}
                        >
                          Resend code
                        </Button>
                        <Button variant="ghost" size="sm" type="button" onClick={resetSignIn}>
                          Use a different email
                        </Button>
                      </div>
                    </div>
                  </form>
                )}
    </StandaloneCard>
  )
}
function ChooseOrganizationStep() {
  const clerk = useClerk()
  const { user } = useUser()
  const { isLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  })
  const [selectingOrganizationId, setSelectingOrganizationId] = useState<string | null>(null)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  useEffect(() => {
    if (!shouldRequestNextMembershipPage({ isLoaded, userMemberships })) return
    userMemberships.fetchNext?.()
  }, [isLoaded, userMemberships])
  const view = buildChooseWorkspaceView({
    isLoaded,
    userMemberships,
  })
  async function handleSelect(membership: ChooseWorkspaceMembership) {
    if (selectingOrganizationId || !setActive) return
    setErrorMessage("")
    setSelectingOrganizationId(membership.organizationId)
    const result = await activateChosenOrganization({
      organizationId: membership.organizationId,
      redirectUrl: readLoginRedirectTarget(),
      setActive,
      assignLocation: (url) => {
        window.location.assign(url)
      },
    })
    if (!result.ok) {
      setSelectingOrganizationId(null)
      setErrorMessage(result.message)
    }
  }
  async function handleUseAnotherAccount() {
    if (isSigningOut) return
    setErrorMessage("")
    setIsSigningOut(true)
    const message = await signOutKeepingReturn(clerk)
    if (message) {
      setErrorMessage(message)
      setIsSigningOut(false)
    }
  }
  return (
    <>
      <ChooseWorkspaceView
        view={view}
        signedInAs={user?.primaryEmailAddress?.emailAddress}
        selectingOrganizationId={selectingOrganizationId}
        errorMessage={errorMessage}
        onSelect={handleSelect}
      />
      <div>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          disabled={selectingOrganizationId != null || isSigningOut}
          onClick={handleUseAnotherAccount}
        >
          Use another account
        </Button>
      </div>
    </>
  )
}
function LoginUnavailable() {
  return (
    <StandaloneCard label="Sign in">
      <StandaloneNote tone="info">
        Authentication is not configured in this local environment.
      </StandaloneNote>
    </StandaloneCard>
  )
}
/** Signs out to the login page, keeping the page the user asked for. Returns an error message on failure. */
async function signOutKeepingReturn(clerk: ReturnType<typeof useClerk>): Promise<string | null> {
  try {
    await clerk.signOut({ redirectUrl: loginPathReturningTo(readLoginRedirectTarget()) })
    return null
  } catch (error) {
    return readAuthError(error)
  }
}
function readLoginRedirectTarget() {
  if (typeof window === "undefined") return "/"
  return resolveLoginRedirectUrl(readLoginRedirectUrlParam(window.location.search))
}
function LoginMessages({
  errorMessage,
  statusMessage,
}: {
  errorMessage: string
  statusMessage: string
}) {
  if (errorMessage) {
    return (
      <StandaloneNote tone="error">{errorMessage}</StandaloneNote>
    )
  }
  if (statusMessage) {
    return (
      <StandaloneNote tone="info">{statusMessage}</StandaloneNote>
    )
  }
  return null
}
function readAuthError(error: unknown) {
  if (error && typeof error === "object") {
    const record = error as {
      longMessage?: unknown
      message?: unknown
    }
    if (typeof record.longMessage === "string" && record.longMessage) return record.longMessage
    if (typeof record.message === "string" && record.message) return record.message
  }
  return "Unable to sign in."
}
function isAlreadySignedInMessage(message: string) {
  return message.toLowerCase().includes("already signed in")
}
function readSignInStatus(result: unknown, fallback: SignInStatus) {
  if (result && typeof result === "object" && "status" in result) {
    const status = (
      result as {
        status?: unknown
      }
    ).status
    if (isSignInStatus(status)) return status
  }
  return fallback
}
function readSupportedSecondFactors(result: unknown, fallback: SignInFactor[]) {
  if (result && typeof result === "object" && "supportedSecondFactors" in result) {
    const factors = (
      result as {
        supportedSecondFactors?: unknown
      }
    ).supportedSecondFactors
    if (Array.isArray(factors)) return factors.filter(isSignInFactor)
  }
  return fallback
}
function isSignInStatus(status: unknown): status is SignInStatus {
  return (
    status === "needs_identifier" ||
    status === "needs_first_factor" ||
    status === "needs_second_factor" ||
    status === "needs_client_trust" ||
    status === "needs_protect_check" ||
    status === "needs_new_password" ||
    status === "complete"
  )
}
function isSignInFactor(factor: unknown): factor is SignInFactor {
  return Boolean(factor && typeof factor === "object" && "strategy" in factor)
}
